/**
 * useIsTouchPhone — single source of truth for "is this a phone-sized touch
 * surface", replacing the ad-hoc `window.innerWidth <= 768` checks scattered
 * across the app.
 *
 * The old width-only test was wrong for landscape phones: modern phones exceed
 * 768px logical width in landscape (Pixel 6 ≈914, iPhone 14 ≈844), so they were
 * treated as desktop — and got the wide desktop panels crammed into the 320px
 * landscape rail the CSS already builds. This query mirrors the CSS breakpoints
 * exactly (App.css portrait ≤768 + the `(orientation: landscape) and
 * (max-height: 500px)` phone-landscape rule) so JS and CSS always agree.
 * See docs/mobile-ux-uplift-plan.md §1.5.
 */

import { useState, useEffect } from 'react';

const TOUCH_PHONE_QUERY =
  '(max-width: 768px), (orientation: landscape) and (max-height: 500px)';

/** Imperative check for use outside React (event handlers, one-off branches). */
export function isTouchPhoneNow(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(TOUCH_PHONE_QUERY).matches;
}

/** Reactive hook — re-renders on viewport/orientation changes. */
export function useIsTouchPhone(): boolean {
  const [isTouchPhone, setIsTouchPhone] = useState(isTouchPhoneNow);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(TOUCH_PHONE_QUERY);
    const onChange = () => setIsTouchPhone(mql.matches);
    mql.addEventListener('change', onChange);
    onChange(); // sync in case it changed between render and effect
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isTouchPhone;
}
